import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Mail,
  RefreshCw,
  CheckCircle2,
  AlertTriangle,
  Clock,
  AlertOctagon,
  MailX,
} from "lucide-react";

/**
 * Reports card that surfaces recent outbound emails logged by the backend.
 * Every PO, digest, renewal, and bug-report send drops a row in the
 * email_events collection at send-time; the Refresh button calls the
 * backend which in turn asks Resend for the current status per row. This
 * lets admins spot bounces / complaints without leaving the app or
 * opening the Resend dashboard.
 */

const PURPOSE_LABEL = {
  po: "PO",
  digest: "Digest",
  renewal: "Renewal",
  bug_report: "Bug",
};

const STATUS_META = {
  delivered: { label: "Delivered", cls: "bg-green-100 text-green-800 border-green-500", Icon: CheckCircle2 },
  sent: { label: "Sent", cls: "bg-blue-100 text-blue-800 border-blue-500", Icon: Clock },
  bounced: { label: "Bounced", cls: "bg-red-100 text-red-800 border-red-500", Icon: MailX },
  complained: { label: "Complained", cls: "bg-orange-100 text-orange-800 border-orange-500", Icon: AlertOctagon },
  failed: { label: "Failed", cls: "bg-red-100 text-red-800 border-red-500", Icon: AlertOctagon },
  opened: { label: "Opened", cls: "bg-green-100 text-green-800 border-green-500", Icon: CheckCircle2 },
  clicked: { label: "Clicked", cls: "bg-green-100 text-green-800 border-green-500", Icon: CheckCircle2 },
};

const formatDate = (iso) => {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    const now = new Date();
    const diffMin = Math.floor((now - d) / 60000);
    if (diffMin < 60) return `${Math.max(1, diffMin)}m ago`;
    if (diffMin < 60 * 24) return `${Math.floor(diffMin / 60)}h ago`;
    return d.toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

const StatusPill = ({ status }) => {
  const meta = STATUS_META[(status || "sent").toLowerCase()] || {
    label: status || "Unknown",
    cls: "bg-gray-100 text-gray-800 border-gray-500",
    Icon: Clock,
  };
  const { Icon } = meta;
  return (
    <span
      data-testid={`email-status-${status}`}
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-bold border ${meta.cls}`}
    >
      <Icon size={11} strokeWidth={2.5} />
      {meta.label}
    </span>
  );
};

const EmailEventsCard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/email-events", { params: { limit: 100 } });
      setData(data);
    } catch (e) {
      // Non-managers get 403 — silently ignore.
      if (e.response?.status !== 403) {
        toast.error("Could not load email events");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const refresh = async () => {
    setRefreshing(true);
    try {
      const { data: r } = await api.post("/email-events/refresh");
      toast.success(
        r.refreshed
          ? `Refreshed ${r.refreshed} row${r.refreshed === 1 ? "" : "s"}`
          : "All statuses already up to date",
      );
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Refresh failed");
    } finally {
      setRefreshing(false);
    }
  };

  if (!data) {
    return (
      <div className="nb-card p-5" data-testid="email-events-card">
        <div className="text-sm text-[#607087]">
          {loading ? "Loading recent deliveries…" : "Recent deliveries unavailable"}
        </div>
      </div>
    );
  }

  const { events, counts, bounced_today: bouncedToday } = data;
  const bouncedTotal = counts?.bounced || 0;

  return (
    <div className="nb-card p-5" data-testid="email-events-card">
      <div className="flex items-start justify-between gap-3 flex-wrap mb-2">
        <div className="flex items-center gap-2">
          <Mail size={20} strokeWidth={2.5} className="text-[#228EBF]" />
          <div>
            <h2 className="font-display text-xl">Recent deliveries</h2>
            <div className="text-xs text-[#607087]">
              Every outbound PO, digest, renewal & bug email — with live Resend status
            </div>
          </div>
        </div>
        <button
          data-testid="email-events-refresh"
          onClick={refresh}
          disabled={refreshing}
          className="nb-btn bg-white px-3 py-2 flex items-center gap-2 text-sm"
          title="Ask Resend for the current status of every recent row"
        >
          <RefreshCw
            size={14}
            strokeWidth={2.5}
            className={refreshing ? "animate-spin" : ""}
          />
          Refresh statuses
        </button>
      </div>

      {/* Summary strip */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-3">
        <div
          className="border-2 border-black rounded-xl p-2.5 bg-white"
          data-testid="email-events-summary-total"
        >
          <div className="label-eyebrow">Total logged</div>
          <div className="font-mono-num text-xl font-bold mt-0.5">
            {data.total}
          </div>
        </div>
        <div
          className={`border-2 border-black rounded-xl p-2.5 ${
            bouncedToday > 0 ? "bg-red-50" : "bg-white"
          }`}
          data-testid="email-events-summary-bounced-today"
        >
          <div className="label-eyebrow flex items-center gap-1">
            {bouncedToday > 0 && (
              <AlertTriangle size={11} strokeWidth={2.5} className="text-red-600" />
            )}
            Bounced today
          </div>
          <div
            className={`font-mono-num text-xl font-bold mt-0.5 ${
              bouncedToday > 0 ? "text-red-700" : ""
            }`}
          >
            {bouncedToday}
          </div>
        </div>
        <div className="border-2 border-black rounded-xl p-2.5 bg-white">
          <div className="label-eyebrow">Bounced (all)</div>
          <div
            className={`font-mono-num text-xl font-bold mt-0.5 ${
              bouncedTotal > 0 ? "text-red-700" : ""
            }`}
          >
            {bouncedTotal}
          </div>
        </div>
        <div className="border-2 border-black rounded-xl p-2.5 bg-white">
          <div className="label-eyebrow">Delivered</div>
          <div className="font-mono-num text-xl font-bold mt-0.5 text-green-700">
            {counts?.delivered || 0}
          </div>
        </div>
      </div>

      {/* Events table */}
      {events.length === 0 ? (
        <div
          className="border-2 border-dashed border-black rounded-xl p-5 text-center text-sm text-[#607087]"
          data-testid="email-events-empty"
        >
          No emails logged yet. Sends from v106 onwards will appear here.
        </div>
      ) : (
        <div className="overflow-x-auto scrollbar-clean">
          <table
            className="w-full text-sm"
            data-testid="email-events-table"
          >
            <thead>
              <tr className="border-b-2 border-black">
                <th className="text-left py-2 px-2 label-eyebrow">Sent</th>
                <th className="text-left py-2 px-2 label-eyebrow">Type</th>
                <th className="text-left py-2 px-2 label-eyebrow">To</th>
                <th className="text-left py-2 px-2 label-eyebrow">Subject</th>
                <th className="text-left py-2 px-2 label-eyebrow">Status</th>
              </tr>
            </thead>
            <tbody>
              {events.map((e) => (
                <tr
                  key={e.id}
                  data-testid={`email-event-row-${e.id}`}
                  className={`border-b border-black/10 hover:bg-[#F4F6FA] ${
                    (e.status || "").toLowerCase() === "bounced"
                      ? "bg-red-50/50"
                      : ""
                  }`}
                >
                  <td
                    className="py-2 px-2 font-mono-num text-xs whitespace-nowrap"
                    title={e.sent_at}
                  >
                    {formatDate(e.sent_at)}
                  </td>
                  <td className="py-2 px-2">
                    <span className="label-eyebrow px-1.5 py-0.5 rounded border border-black bg-white">
                      {PURPOSE_LABEL[e.purpose] || e.purpose}
                    </span>
                  </td>
                  <td
                    className="py-2 px-2 font-mono text-xs max-w-[220px] truncate"
                    title={e.to}
                  >
                    {e.to}
                  </td>
                  <td
                    className="py-2 px-2 max-w-[280px] truncate"
                    title={e.subject}
                  >
                    {e.subject}
                  </td>
                  <td className="py-2 px-2">
                    <StatusPill status={e.status} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default EmailEventsCard;
